import { RepositoryRuntimeVersion } from "@back4app2/sdk";

import TextInput from "../FormInputField/TextInput/TextInput";

// A pin is written into the repository's own version file at build time, so it
// has to be a version and nothing else. Mirrors isValidRuntimeVersionPin on the
// API — the server rejects anything else, and finding that out after submitting
// a whole form is a bad way to learn it.
const PIN_PATTERN = /^\d+(\.\d+){0,2}$/;

export const isValidRuntimePin = (value: string): boolean =>
  PIN_PATTERN.test(value);

interface RuntimeVersionFieldProps {
  // Named for the user: "Node.js", "Java". Absent while the detection runs.
  detectedStack?: string;
  // What the detection found, or nothing while it is still running.
  resolved?: RepositoryRuntimeVersion;
  // The user's pin. Empty means follow the repository, which is the default
  // and the state every app starts in.
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

// What runtime this app will build on, and where that came from.
//
// The field is EMPTY by default and stays that way unless someone types in it.
// Prefilling it with the detected version would look helpful and quietly turn
// every app into a pinned app: the value would then beat the repository, so a
// version added to the repo later would stop taking effect — the exact silent
// override this whole feature exists to remove.
const RuntimeVersionField = ({
  detectedStack,
  resolved,
  value,
  onChange,
  disabled,
}: RuntimeVersionFieldProps) => {
  if (!resolved || !detectedStack) {
    return null;
  }

  const invalid = !!value && !isValidRuntimePin(value);
  const stack = detectedStack;

  // Where the version came from, and nothing else.
  //
  // Not the version itself: the field below carries it as its placeholder,
  // and repeating it in the line under the field was the same number twice,
  // three words apart. Not a clause around it either — "Your repository asks
  // for Node.js 22 in package.json, and the build uses it" is subject and verb
  // wrapped around one fact, under a field labelled Runtime Version, about a
  // repository the reader owns.
  //
  // The exceptions are the two branches where something is WRONG or about to
  // surprise: a declaration the build cannot read, and a version that is
  // ignored outright. Those keep their sentence — and their version, because
  // the one in the field is not the one that will be built.
  let status;
  /**
   * Whether this line is something to DO, or something to know.
   *
   * Something to know goes behind the (i) beside the label: which version this
   * builds on and where that came from is true forever, read on the first app
   * and never again, and it was a line under the field on every visit.
   *
   * Something to do stays on the screen, because a person who did not know to
   * press the icon never reads what is behind it. There are two: a declared
   * version the build cannot read — the repository says one thing, the build
   * does another, and only a different file fixes it — and no declaration at
   * all, where the build takes the newest release and an app written for an
   * older major breaks on a day nobody chose.
   */
  let isProblem = false;

  if (value && !invalid) {
    status = <>Pinned here, over whatever your repository declares.</>;
  } else if (resolved.source === "REPO" && resolved.honored) {
    status = <>From {resolved.origin}, and the build uses it.</>;
  } else if (resolved.source === "REPO" && resolved.canPin) {
    // Declared somewhere the buildpack never opens — which the build now
    // repairs by copying the value into the file it does read. Said out loud
    // because the version in the log will not match the file they edited.
    status = (
      <>
        Your repository asks for <strong>{stack} {resolved.version}</strong> in{" "}
        {resolved.origin}. The build reads {resolved.buildpackReads}, so we copy
        it across for you.
      </>
    );
  } else if (resolved.source === "REPO") {
    // Declared, not read, and not something the platform can write either.
    // The only honest thing left is to name the file that would work.
    isProblem = true;
    status = (
      <>
        Your repository asks for <strong>{stack} {resolved.version}</strong> in{" "}
        {resolved.origin}, but the build reads {resolved.buildpackReads} — so
        that version is ignored. Declare it in {resolved.declareIn} to build on
        it.
      </>
    );
  } else if (resolved.version) {
    status = <>Back4app's default, since your repository pins none.</>;
  } else {
    // No declaration and no platform default: the buildpack picks, and it picks
    // the newest release. Worth saying out loud — it is the one that breaks
    // apps built for an older major.
    isProblem = true;
    status = (
      <>
        Your repository doesn't pin a version, so the build uses the{" "}
        <strong>newest {stack} release</strong>.{" "}
        {resolved.canPin
          ? "Set a version here if your app needs an older one."
          : `Declare it in ${resolved.declareIn} if your app needs an older one.`}
      </>
    );
  }

  // No input where a pin would do nothing — but the label stays. Without it
  // the section is a grey sentence floating between two fields, and the answer
  // to "what runtime am I getting?" reads as missing rather than as given.
  // Same label markup as TextInput, so the two line up in the form.
  if (!resolved.canPin) {
    return (
      <div className="flex flex-col gap-2 w-full">
        <span className="inline-flex items-center justify-start gap-2 font-medium leading-140 capitalize">
          Runtime Version
        </span>
        <div className="text-ds-115 leading-[1.5] text-navy-200">{status}</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <TextInput
        value={value}
        label="Runtime Version"
        name="runtimeVersion"
        onChange={(e) => onChange(e.target.value)}
        placeholderText={resolved.version || "e.g. 20"}
        infoTip={isProblem ? undefined : status}
        isError={invalid}
        errorMessage={
          invalid ? 'Use a version number, like "20" or "3.11".' : ""
        }
        isDisabled={disabled}
      />
      {/* Helper size, like every other hint: `text-sm` rendered larger than
          the field's own label on a phone. */}
      {isProblem ? (
        <div className="text-ds-115 leading-[1.5] text-navy-200">{status}</div>
      ) : null}
    </div>
  );
};

export default RuntimeVersionField;
